import React, { useEffect, useMemo, useState } from 'react';
import _ from 'lodash';
import dayjs from 'dayjs';
import {
  KCWA_CUSTOMER_CENTER_PHONE,
  WHEEL_TYPE_OPTIONS,
} from '../../constants/WheelInspectionConstants';
import { WHEEL_PREMIUM_WARRANTY_OVERLAY } from '../../constants/WheelPremiumWarrantyOverlay';
import { parseWheelInspection } from '../../utils/wheelInspectionUtil';
import { CommonUtil } from '../../utils/commonUtil';
import { ServiceProvider } from '../../services';
import warrantyTemplate from '../../assets/images/wheel/wheel-premium-warranty-template.png';
import './WheelPremiumWarrantyReport.scss';

const shopService = ServiceProvider.shop;
const userService = ServiceProvider.user;

const POSITION_CODES = ['FL', 'RL', 'FR', 'RR'];

const resolveImageSrc = (imagePath) => {
  if (!imagePath) return '';
  if (_.startsWith(imagePath, 'http')) return imagePath;
  return process.env.REACT_APP_S3_BASE_URL + imagePath;
};

const displayValue = (value) => {
  if (value === null || value === undefined || value === '') return '';
  return String(value);
};

const formatPhone = (value) => {
  const raw = displayValue(value);
  return raw ? CommonUtil.Formatter.phone(raw) : '';
};

const OverlayText = ({ style, children, className = '' }) => (
  <span
    className={`wheel-premium-warranty__overlay-text ${className}`.trim()}
    style={style}
  >
    {children}
  </span>
);

const OverlayCheck = ({ style, checked }) =>
  checked ? (
    <span className="wheel-premium-warranty__overlay-check" style={style}>
      ✓
    </span>
  ) : null;

const WheelPremiumWarrantyReport = ({
  data = {},
  vehicleInformation = {},
  inspector = {},
  customerCompany = {},
  customer = {},
}) => {
  const inspection = parseWheelInspection(vehicleInformation);
  const selectedPositions = _.get(inspection, 'preRestoration.positions', []);
  const selectedWheelTypes = _.get(inspection, 'safety.wheelTypes', []);

  const checkDate = _.get(data, 'CHCK_DE');
  const formattedDate = checkDate ? dayjs(checkDate, 'YYYYMMDD') : dayjs();

  const [shop, setShop] = useState(null);
  const [inspectorCertificateUrl, setInspectorCertificateUrl] = useState('');

  useEffect(() => {
    let cancelled = false;

    async function loadShop() {
      const shopId = _.get(inspector, 'shopId');
      if (!shopId) return;
      try {
        const { data: shopData } = await shopService.getData(shopId);
        if (!cancelled) setShop(shopData);
      } catch (error) {
        console.log('shop load fail');
      }
    }

    async function loadInspectorCertificate() {
      const userId = _.get(inspector, 'userId');
      if (!userId) return;
      try {
        const {
          data: { officialSealURL },
        } = await userService.getDataExternal(userId);
        if (officialSealURL) {
          const imageSrc = resolveImageSrc(officialSealURL);
          if (!cancelled) setInspectorCertificateUrl(imageSrc);
        }
      } catch (error) {
        console.log('inspector certificate load fail');
      }
    }

    loadShop();
    loadInspectorCertificate();

    return () => {
      cancelled = true;
    };
  }, [inspector]);

  const shopName = useMemo(
    () =>
      displayValue(
        _.get(shop, 'shopName') || _.get(inspector, 'shopName')
      ),
    [shop, inspector]
  );

  const shopAddress = useMemo(() => {
    const address = displayValue(_.get(shop, 'address'));
    const addressDetail = displayValue(_.get(shop, 'addressDetail'));
    return [address, addressDetail].filter(Boolean).join(' ');
  }, [shop]);

  const shopPhone = useMemo(() => {
    const raw =
      _.get(shop, 'mainPhone') ||
      _.get(shop, 'cellphone') ||
      _.get(shop, 'phone');
    return formatPhone(raw);
  }, [shop]);

  const customerName = useMemo(
    () =>
      displayValue(
        _.get(customer, 'customerName') ||
          _.get(customer, 'customCustomerName') ||
          _.get(data, 'NTIC_PERSON_NM') ||
          _.get(customerCompany, 'companyName')
      ),
    [customer, data, customerCompany]
  );

  const overlay = WHEEL_PREMIUM_WARRANTY_OVERLAY;

  return (
    <div className="wheel-premium-warranty">
      <div className="wheel-premium-warranty__canvas">
        <img
          src={warrantyTemplate}
          alt="휠 복원 프리미엄 보증서"
          className="wheel-premium-warranty__template"
        />

        <OverlayText style={overlay.carModel}>
          {displayValue(_.get(data, 'CNM'))}
        </OverlayText>
        <OverlayText style={overlay.carNumber}>
          {displayValue(_.get(data, 'VHRNO'))}
        </OverlayText>
        <OverlayText style={overlay.contact}>
          {formatPhone(_.get(customerCompany, 'mainPhone'))}
        </OverlayText>

        {POSITION_CODES.map((code) => (
          <OverlayCheck
            key={code}
            checked={selectedPositions.includes(code)}
            style={overlay.positions[code]}
          />
        ))}

        {WHEEL_TYPE_OPTIONS.map((option) => (
          <OverlayCheck
            key={option.value}
            checked={selectedWheelTypes.includes(option.value)}
            style={overlay.wheelTypes[option.value]}
          />
        ))}

        {inspectorCertificateUrl && (
          <div
            className="wheel-premium-warranty__overlay-certificate-wrap"
            style={overlay.certImage}
          >
            <img
              src={inspectorCertificateUrl}
              alt="관리사 자격증"
              className="wheel-premium-warranty__overlay-certificate"
              crossOrigin="anonymous"
            />
          </div>
        )}

        <OverlayText style={overlay.shopName}>{shopName}</OverlayText>
        <OverlayText style={overlay.shopAddress} className="multiline">
          {shopAddress}
        </OverlayText>
        <OverlayText style={overlay.shopPhone}>{shopPhone}</OverlayText>

        <OverlayText style={overlay.dateYear} className="text-center">
          {formattedDate.format('YYYY')}
        </OverlayText>
        <OverlayText style={overlay.dateMonth} className="text-center">
          {formattedDate.format('MM')}
        </OverlayText>
        <OverlayText style={overlay.dateDay} className="text-center">
          {formattedDate.format('DD')}
        </OverlayText>

        <OverlayText style={overlay.customerName}>{customerName}</OverlayText>

        <OverlayText
          style={overlay.customerCenter}
          className="customer-center"
        >
          고객센터 {KCWA_CUSTOMER_CENTER_PHONE}
        </OverlayText>
      </div>
    </div>
  );
};

export default WheelPremiumWarrantyReport;
